import { z } from "zod";

/**
 * Zod schema for the validator-hop stall marker written into the
 * `jobs.last_error` JSONB column (typed `unknown` at the column
 * boundary in `@prospect/db`).
 *
 * The shape `{ source: "validator_hop", reason }` was chosen in
 * `docs/decisions/2026-05-10-validator-hop-stall-recovery.md` (Decision
 * 1) over a typed enum column — a tagged JSONB blob keeps the recovery
 * marker out of the schema while still being machine-readable. This
 * schema is the single source of truth for that shape; it is written by
 * `markValidatorHopStalled` (orchestrator) and read at two boundaries:
 *
 * - The orchestrator retry precondition (`safeParse` in
 *   `services/orchestrator/src/routes/retry-validator.ts`) gates the 409
 *   `not_stalled` response and the re-failure check.
 * - The admin-UI banner reader (`parseValidatorHopStall` in
 *   `apps/admin-ui/src/routes/JobDetail.tsx`) gates the amber "stalled"
 *   indicator + "Retry validator" banner.
 *
 * Consolidating these readers behind one schema satisfies the
 * "Zod at every boundary" invariant for the otherwise-`unknown` column.
 */
export const validatorHopStallSchema = z.object({
  source: z.literal("validator_hop"),
  reason: z.string(),
});

/** The parsed validator-hop stall marker — see {@link validatorHopStallSchema}. */
export type ValidatorHopStall = z.infer<typeof validatorHopStallSchema>;

/**
 * Build a validator-hop stall marker for writing into `jobs.last_error`.
 * The single construction site for the `{ source: "validator_hop" }`
 * discriminant — `markValidatorHopStalled` (orchestrator) calls this so
 * the literal lives in exactly one place.
 */
export function buildValidatorHopStall(reason: string): ValidatorHopStall {
  return { source: "validator_hop", reason };
}

/**
 * Zod schema for the autonomous-curator zero-drafts flake marker written
 * into `jobs.last_error` when the orchestrator's `applyAgentOutput`
 * detects the flake shape captured in
 * [issue #659](https://github.com/PROSPECT-TECH-LTD/prospect-platform/issues/659):
 * autonomous curator reports `status: success` with terminal
 * `mode_progress` events but `newDraftsAdded === 0`. Companion to the
 * [#638](https://github.com/PROSPECT-TECH-LTD/prospect-platform/issues/638)
 * curator-side guard, which covers the mode_1-doesn't-reach-terminal
 * variant.
 *
 * Shape mirrors {@link validatorHopStallSchema} — a discriminant on
 * `source` plus a free-form `reason` — with a `kind` literal that
 * pins the specific fire condition (`zero_drafts_produced` is the
 * only one today; the issue's acceptance criteria named it explicitly
 * so post-mortems can `SELECT ... WHERE last_error->>kind = ...`
 * without pattern-matching). Written by the orchestrator's
 * `applyAgentOutput` via {@link buildCuratorAutonomousFlake};
 * consumed by post-mortem SQL and any future admin-UI banner that
 * surfaces the flake.
 */
export const curatorAutonomousFlakeSchema = z.object({
  source: z.literal("curator_autonomous_flake"),
  kind: z.literal("zero_drafts_produced"),
  reason: z.string(),
});

/** The parsed curator-autonomous-flake marker — see {@link curatorAutonomousFlakeSchema}. */
export type CuratorAutonomousFlake = z.infer<typeof curatorAutonomousFlakeSchema>;

/**
 * Build a curator-autonomous-flake marker for writing into
 * `jobs.last_error`. The single construction site for the
 * `{ source: "curator_autonomous_flake" }` discriminant — the
 * orchestrator's `applyAgentOutput` calls this so the literal lives in
 * exactly one place. Only `zero_drafts_produced` is defined today;
 * add a new `kind` literal to the schema before introducing a second
 * builder.
 */
export function buildCuratorAutonomousFlake(
  reason: string,
): CuratorAutonomousFlake {
  return {
    source: "curator_autonomous_flake",
    kind: "zero_drafts_produced",
    reason,
  };
}
